import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { useCurrency } from '@/Hooks/useCurrency';
import { usePermission } from '@/Hooks/usePermission';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Field, Select } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { EmptyState } from '@/components/ui/states';
import { type CatalogItem, type Category, type Menu, apiError, isRateLimited } from '../lib';
import { Breadcrumb } from '@/components/ui/breadcrumb';
import { Stepper } from '@/components/ui/stepper';
import { SwitchRow } from '@/components/ui/switch';
import { WizardFooter } from './chrome';
import { RadioGroup } from './controls';
import { ScheduleEditor } from './ScheduleEditor';
import { ItemPicker } from './ItemPicker';
import { ReviewStep } from './ReviewStep';
import {
  BLANK_DRAFT,
  DEFAULT_WINDOW,
  STEPS,
  type Blocker,
  type ItemBatch,
  type MenuDraft,
  type MenuSnapshot,
  type StepId,
  type WizardMode,
  attachItemBodies,
  blockerFor,
  createMenuBody,
  draftFromSnapshot,
  itemBatches,
  itemDiff,
  menuChanges,
  menuFieldChanges,
  nextStep,
  prevStep,
  rebaseSaved,
  snapshotOf,
  stepIndex,
  submitBlocker,
  updateMenuBody,
} from './wizardState';

interface Branch {
  id: string;
  name: string;
}

/**
 * The one batch a save could not get past.
 *
 * `POST /menus/:id/items/bulk` and `DELETE /menus/:id/items/bulk` are each one
 * transaction, so a refused batch is guaranteed to have applied nothing —
 * there is no "may or may not exist" to hedge about any more. Every id in it,
 * plus every id in a batch this run never got to, is exactly what stays
 * selected, and `index`/`count` are what let the message say which one.
 */
interface BatchFailure {
  message: string;
  rateLimited: boolean;
  /** 1-based position of the failed batch among the ones this save attempted. */
  index: number;
  count: number;
  /** Ids riding in the failed batch itself, not counting the ones after it. */
  itemCount: number;
}

/**
 * The four-screen menu wizard — Basic info, Schedule, Add items, Review — in
 * both modes. Pass a `menu` and it edits that menu; pass none and it creates one.
 *
 * ## Why this is not a route
 *
 * Every frame is drawn as a routed page with a breadcrumb, and the app has flat
 * list routes only (`Routes.tsx`); converting these four would be the first
 * child route in the product and a decision all five wave-2 groups have to make
 * the same way, or the refactor produces five dialects. It renders in place
 * instead, which needs no router change and no change to any file this group
 * does not own. The breadcrumb is still drawn, and still true — it is the
 * position that is real, not the URL. If the routing decision lands later, the
 * change is one wrapper.
 *
 * ## Why one component and not two
 *
 * The steps are identical; only three things differ, and each is a parameter
 * rather than a copy: where the draft is **seeded** from, what **submit** sends,
 * and what the review step is allowed to **promise**. A `MenuEditWizard.tsx`
 * would have had to re-learn `neverOpens`, the sold-out badge and the
 * tenant-wide radio, and would have drifted the first time one of them changed.
 *
 * ## What the frames drew and this does not build
 *
 * - **Description.** `Menu` has no such column and neither DTO has such a field,
 *   and `main.ts` runs `forbidNonWhitelisted: true` — sending it would 400 the
 *   entire request, not drop the value.
 * - **"Area menu".** `Area` has no menu relation, and area-scoped catalog
 *   behaviour was deleted in backend `7978703` after the owner refused it. The
 *   radio survives with the axis the code has: branch-scoped vs tenant-wide.
 * - **Seasonal override dates.** `MenuSchedule.startsOn/endsOn` exist on a table
 *   nothing writes; neither DTO declares them, so they 400 too.
 *
 * ## What the frames did not draw and this keeps
 *
 * `isDefault` (which menu a guest QR lands on), the tenant-wide scope (the
 * fallback `resolveActiveMenu` uses when no branch menu matches), both window
 * warnings, the per-window delete, the sold-out badge, and every empty and
 * refusal state — none of which appear on any of the four frames, and all of
 * which change what the operator ships.
 */
export function MenuWizard({
  items,
  menu,
  onCancel,
  onDone,
  otherMenusExist,
}: {
  items: CatalogItem[];
  /**
   * The menu being edited, as `GET /menus/:id` returned it — schedule and
   * attached rows included, so seeding costs no second request. Absent means
   * create.
   */
  menu?: Menu;
  onCancel: () => void;
  /** `complete` is false when the menu itself is saved but some item request was
   *  refused — the caller should refresh and select it, and leave the wizard up. */
  onDone: (menuId: string, complete: boolean) => void;
  /** Whether *another* menu exists that could win guest resolution — on an edit
   *  that means "another besides this one". */
  otherMenusExist: boolean;
}) {
  const { t } = useTranslation();
  // `basePrice` is the catalog's tenant-level price, not a branch override, so
  // it reads in the tenant's base currency even on a branch-scoped menu.
  const { format: formatMoney } = useCurrency(null);
  const { can } = usePermission();

  const mode: WizardMode = menu ? 'edit' : 'create';
  const permission = mode === 'edit' ? 'menu.update' : 'menu.create';
  const canSubmit = can(permission);
  // Attaching, detaching and reordering all sit behind `menu.update`, on both
  // paths — so on an edit the two are the same permission.
  const canAttach = can('menu.update');

  const [step, setStep] = useState<StepId>('basic');
  /**
   * What the server holds. Captured once, on mount, and then advanced only by
   * what actually lands — never re-synced from the prop, because the operator's
   * unsaved edits live in `draft` and a re-seed would silently discard them.
   */
  const [saved, setSaved] = useState<MenuSnapshot | null>(() => (menu ? snapshotOf(menu) : null));
  const [draft, setDraft] = useState<MenuDraft>(() => (saved ? draftFromSnapshot(saved) : BLANK_DRAFT));
  const [branches, setBranches] = useState<Branch[]>([]);
  const [branchesRefused, setBranchesRefused] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);

  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [failures, setFailures] = useState<{ ok: number; refused: number; batch: BatchFailure } | null>(
    null,
  );
  /** Create: the menu that now exists. Edit: whether the PATCH already landed,
   *  so a retry after a refused item request does not re-audit an identical
   *  `menu.update`. */
  const [createdId, setCreatedId] = useState<string | null>(null);
  const [fieldsSaved, setFieldsSaved] = useState(false);

  const patch = (next: Partial<MenuDraft>) => setDraft((current) => ({ ...current, ...next }));

  useEffect(() => {
    // `GET /branches` requires `branches.view` — not `settings.view`, whatever
    // the old comment on this tab said. A catalog manager may not hold it, and
    // an empty required picker would otherwise deadlock the wizard, so the
    // refusal is remembered and rendered rather than swallowed.
    api
      .get<{ data: Branch[] }>('/branches', { params: { limit: 100 } })
      .then(({ data }) => setBranches(data.data ?? []))
      .catch(() => setBranchesRefused(true));
    // Categories drive the filter chips only; the tab is handed the items but
    // not the categories, so this reads them itself. An empty list just means
    // no chips.
    api
      .get<{ data: Category[] }>('/categories', { params: { limit: 200 } })
      .then(({ data }) => setCategories(data.data ?? []))
      .catch(() => setCategories([]));
  }, []);

  /**
   * Branch names, seeded with the edited menu's own branch.
   *
   * Without that seed a refused `GET /branches` would leave the review step
   * reporting a cuid where the operator expects a branch name — on the one
   * screen whose entire job is to be checkable.
   */
  const branchNames = useMemo(() => {
    const map = new Map(branches.map((branch) => [branch.id, branch.name]));
    if (menu?.branch) map.set(menu.branch.id, menu.branch.name);
    return map;
  }, [branches, menu]);

  const branchLabel = (branchId: string | null) =>
    branchId ? (branchNames.get(branchId) ?? branchId) : t('catalog.menus.tenantWide');

  const diff = useMemo(
    () => (saved ? itemDiff(saved.items, draft.items) : null),
    [saved, draft.items],
  );
  const changes = useMemo(
    () => (saved && diff ? menuChanges(saved, draft, diff) : []),
    [saved, draft, diff],
  );

  /**
   * Why the wizard will not move on, said out loud next to the button rather
   * than hidden in a `title` — a disabled control that cannot explain itself is
   * the same dead end whichever step it is on.
   */
  const reasonFor = (blocker: Blocker | null) => {
    if (!blocker) return null;
    // `catalog.stations.chooseBranch` already ships in both locales and says
    // exactly this; a new key here would have been English-only in Arabic.
    if (blocker === 'branchRequired') return t('catalog.stations.chooseBranch');
    return t(`catalog.menus.${blocker}`);
  };

  const blockedReason =
    reasonFor(step === 'review' ? submitBlocker(draft) : blockerFor(step, draft)) ??
    // An edit with nothing to save is not an error, but it is not a save either,
    // and a button that fires five identical writes is worse than one that says
    // why it is idle.
    (step === 'review' && mode === 'edit' && changes.length === 0
      ? t('catalog.menus.wizard.edit.noChanges')
      : null);

  /**
   * Runs the item half of a save through `POST`/`DELETE /menus/:id/items/bulk`,
   * one batch at a time, and stops at the first one the server refuses.
   *
   * Every batch before the failed one is guaranteed to have landed — each is
   * one transaction, so there is nothing to infer about it — and every id from
   * the failed batch onward, including a batch this run never got to attempt,
   * is exactly what "still to do" means. That set is `refusedIds`; the caller
   * is what turns it into state (create keeps it in `draft.items`, edit rebases
   * `saved` onto it), because only the caller knows which shape applies.
   */
  const runItemBatches = async (
    menuId: string,
    batches: ItemBatch[],
  ): Promise<{ ok: number; refusedIds: string[]; failure: BatchFailure | null }> => {
    const total = batches.reduce((sum, batch) => sum + batch.itemIds.length, 0);
    setProgress({ done: 0, total });
    let ok = 0;

    for (const [index, batch] of batches.entries()) {
      try {
        if (batch.phase === 'detach') {
          await api.delete(`/menus/${menuId}/items/bulk`, { data: { itemIds: batch.itemIds } });
        } else {
          await api.post(`/menus/${menuId}/items/bulk`, { items: batch.items });
        }
      } catch (error) {
        const refusedIds = batches.slice(index).flatMap((b) => b.itemIds);
        return {
          ok,
          refusedIds,
          failure: {
            message: apiError(error, t('catalog.menus.saveFailed')),
            rateLimited: isRateLimited(error),
            index: index + 1,
            count: batches.length,
            itemCount: batch.itemIds.length,
          },
        };
      }
      ok += batch.itemIds.length;
      setProgress({ done: ok, total });
    }

    return { ok, refusedIds: [], failure: null };
  };

  /**
   * Create — one POST plus a bulk attach, chunked at `ITEMS_BULK_MAX`. The menu
   * is already saved by the time the attach batches run, so a refused batch
   * must not read as "nothing happened" — the id is kept, and the button
   * becomes a retry over exactly the ids that batch (and the batches after it)
   * never got applied.
   */
  const submitCreate = async () => {
    let menuId = createdId;
    try {
      if (!menuId) {
        const { data } = await api.post<{ id: string }>('/menus', createMenuBody(draft));
        menuId = data.id;
        setCreatedId(menuId);
      }
    } catch (error) {
      toast.error(apiError(error, t('catalog.menus.saveFailed')));
      return;
    }

    const batches = canAttach
      ? itemBatches({ attach: attachItemBodies(draft), update: [], detach: [] })
      : [];
    const { ok, refusedIds, failure } = await runItemBatches(menuId, batches);

    if (failure) {
      setFailures({ ok, refused: refusedIds.length, batch: failure });
      // Every id here is guaranteed unattached — the failed batch is one
      // transaction and nothing after it was even attempted — so this is
      // precisely what a retry has to re-send.
      setDraft((current) => ({
        ...current,
        items: current.items.filter((row) => refusedIds.includes(row.itemId)),
      }));
      onDone(menuId, false);
      return;
    }

    toast.success(t('catalog.menus.created'));
    onDone(menuId, true);
  };

  /**
   * Edit — one PATCH, then the item diff run through the same bulk batches.
   *
   * Order is detach, then attach/reorder together (the bulk-attach endpoint
   * upserts, so a new row and a repositioned one are the same request).
   * Detaching first frees the positions the remaining rows may claim.
   *
   * The partial-failure story is create's, made exact at batch granularity
   * instead of item granularity. Create keeps the menu id and drops the ids
   * that landed; here the *snapshot* is rebased onto what landed, so
   * re-running `itemDiff` against it reproduces exactly the refused batch and
   * nothing else. Pressing Save again re-sends exactly that — no re-PATCH of
   * the menu, no re-attach of a row a transaction already committed.
   */
  const submitEdit = async () => {
    if (!menu || !saved || !diff) return;

    if (!fieldsSaved && menuFieldChanges(saved, draft, diff).length > 0) {
      try {
        await api.patch(`/menus/${menu.id}`, updateMenuBody(draft));
        setFieldsSaved(true);
      } catch (error) {
        // Nothing else has run, so nothing is half-applied.
        toast.error(apiError(error, t('catalog.menus.saveFailed')));
        return;
      }
    }

    const batches = canAttach ? itemBatches(diff) : [];
    const { ok, refusedIds, failure } = await runItemBatches(menu.id, batches);

    if (failure) {
      setFailures({ ok, refused: refusedIds.length, batch: failure });
      // Execution only reaches here with the PATCH landed, so the snapshot takes
      // the menu's new fields *and* the batches that committed. What is left in
      // `draft` minus this is exactly the refused batch and whatever followed it.
      const applied = updateMenuBody(draft);
      setSaved({
        name: applied.name,
        branchId: applied.branchId,
        schedule: applied.schedule,
        isDefault: applied.isDefault,
        isActive: applied.isActive,
        items: rebaseSaved(saved.items, diff, refusedIds),
      });
      onDone(menu.id, false);
      return;
    }

    toast.success(t('catalog.menus.updated'));
    onDone(menu.id, true);
  };

  const submit = async () => {
    if (submitBlocker(draft) || !canSubmit || blockedReason) return;
    setSubmitting(true);
    setFailures(null);
    try {
      await (mode === 'edit' ? submitEdit() : submitCreate());
    } finally {
      setSubmitting(false);
      setProgress(null);
    }
  };

  const advance = () => (step === 'review' ? void submit() : setStep(nextStep(step)));

  const headline =
    mode === 'edit' ? t('catalog.menus.editMenu') : t('catalog.menus.newMenu');

  const title = {
    basic: headline,
    schedule: t('catalog.menus.schedule'),
    items: t('catalog.menus.wizard.addItems'),
    review: t('catalog.menus.wizard.reviewTitle'),
  }[step];

  const stepLabels: Record<StepId, string> = {
    basic: t('catalog.menus.wizard.step.basicInfo'),
    schedule: t('catalog.menus.wizard.step.schedule'),
    items: t('catalog.menus.wizard.step.addItems'),
    review: t('catalog.menus.wizard.step.review'),
  };

  return (
    <div className="space-y-5">
      <div className="space-y-1">
        <Breadcrumb
          label={t('nav.catalog')}
          trail={[{ label: t('nav.catalog') }, { label: t('catalog.tabs.menus') }]}
          current={headline}
          className="text-xs"
        />
        <h1 className="text-2xl font-semibold">{title}</h1>
      </div>

      <Stepper
        steps={STEPS.map((id) => ({ id, label: stepLabels[id] }))}
        current={stepIndex(step)}
        onJump={setStep}
      />

      {!canSubmit ? (
        <Card>
          <CardContent className="p-5">
            <EmptyState title={t('operations.kitchen.blocked.permission', { permission })} />
          </CardContent>
        </Card>
      ) : (
        <>
          {step === 'basic' && (
            <Card className="max-w-[40rem]">
              <CardContent className="space-y-5 p-5">
                <Field label={t('catalog.items.name')} required>
                  <Input
                    value={draft.name}
                    onChange={(event) => patch({ name: event.target.value })}
                  />
                </Field>

                <RadioGroup
                  legend={t('catalog.menus.wizard.menuType')}
                  hint={t('catalog.menus.branchHint')}
                  name="menu-scope"
                  value={draft.scope}
                  onChange={(scope) => patch({ scope })}
                  options={[
                    { value: 'branch', label: t('catalog.menus.branchScoped') },
                    { value: 'tenant', label: t('catalog.menus.tenantWide') },
                  ]}
                />

                {draft.scope === 'branch' && (
                  <Field
                    label={t('catalog.menus.branch')}
                    required
                    error={
                      branchesRefused ? t('catalog.menus.wizard.branchesRefused') : undefined
                    }
                  >
                    <Select
                      value={draft.branchId}
                      onChange={(event) => patch({ branchId: event.target.value })}
                    >
                      <option value="">{t('catalog.stations.chooseBranch')}</option>
                      {/* The branch this menu is already on, so a refused
                          `/branches` cannot blank out a field the operator never
                          touched and then save the blank. */}
                      {branches.length === 0 && draft.branchId && (
                        <option value={draft.branchId}>{branchLabel(draft.branchId)}</option>
                      )}
                      {branches.map((branch) => (
                        <option key={branch.id} value={branch.id}>
                          {branch.name}
                        </option>
                      ))}
                    </Select>
                  </Field>
                )}

                <div className="space-y-2 border-t pt-4">
                  <SwitchRow
                    label={t('catalog.items.active')}
                    checked={draft.isActive}
                    onChange={(isActive) => patch({ isActive })}
                  />
                  {/* Not on any frame, and the only control that decides which
                      menu a guest QR resolves to. */}
                  <SwitchRow
                    label={t('catalog.menus.isDefault')}
                    checked={draft.isDefault}
                    onChange={(isDefault) => patch({ isDefault })}
                  />
                </div>
              </CardContent>
            </Card>
          )}

          {step === 'schedule' && (
            <Card className="max-w-[40rem]">
              <CardHeader>
                <CardTitle>{t('catalog.menus.wizard.windows')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* The hint lives on the editor below rather than here: with
                    both, `alwaysOpen` printed twice on one card. */}
                <SwitchRow
                  label={t('catalog.menus.wizard.alwaysAvailable')}
                  checked={draft.alwaysAvailable}
                  onChange={(alwaysAvailable) => patch({ alwaysAvailable })}
                />

                <Button
                  size="sm"
                  variant="outline"
                  disabled={draft.alwaysAvailable}
                  onClick={() => patch({ schedule: [...draft.schedule, { ...DEFAULT_WINDOW }] })}
                >
                  {t('catalog.menus.addWindow')}
                </Button>

                <ScheduleEditor
                  schedule={draft.schedule}
                  disabled={draft.alwaysAvailable}
                  onChange={(schedule) => patch({ schedule })}
                />
              </CardContent>
            </Card>
          )}

          {step === 'items' && (
            <ItemPicker
              items={items}
              categories={categories}
              selected={draft.items}
              onChange={(next) => patch({ items: next })}
              formatMoney={formatMoney}
              readOnly={!canAttach}
            />
          )}

          {step === 'review' && (
            <div className="space-y-4">
              <ReviewStep
                draft={draft}
                items={items}
                before={saved ?? undefined}
                diff={diff ?? undefined}
                branchLabel={branchLabel}
                otherMenusExist={otherMenusExist}
                formatMoney={formatMoney}
                onEdit={setStep}
              />

              {progress && (
                <p className="text-sm text-ink-muted" role="status">
                  {t('catalog.items.bulk.progress', progress)}
                </p>
              )}

              {failures && (
                <div
                  role="alert"
                  className="space-y-2 rounded-xl border border-red-600/40 bg-red-500/10 p-4"
                >
                  <p className="text-sm font-medium text-red-700 dark:text-red-400">
                    {t('catalog.items.bulk.updatePartial', {
                      ok: failures.ok,
                      failed: failures.refused,
                    })}
                  </p>
                  <p className="text-sm text-ink-muted">
                    {t('catalog.menus.wizard.bulk.chunkFailed', {
                      index: failures.batch.index,
                      count: failures.batch.count,
                      items: failures.batch.itemCount,
                      reason: failures.batch.message,
                    })}
                  </p>
                  {failures.batch.rateLimited && (
                    <p className="text-sm text-ink-muted">{t('catalog.menus.wizard.bulk.rateLimited')}</p>
                  )}
                  <p className="text-xs text-ink-muted">{t('catalog.menus.wizard.bulk.atomicHint')}</p>
                </div>
              )}
            </div>
          )}
        </>
      )}

      <WizardFooter
        step={step}
        onCancel={onCancel}
        onBack={() => setStep(prevStep(step))}
        onNext={advance}
        nextLabel={
          step !== 'review'
            ? t('common.next')
            : mode === 'edit'
              ? t('catalog.menus.wizard.edit.save')
              : t('catalog.menus.wizard.create')
        }
        blockedReason={blockedReason}
        busy={submitting}
      />
    </div>
  );
}
